import { useEffect, useMemo, useState } from 'react';
import { Binoculars, EnvelopeSimple, User, Gear } from '@phosphor-icons/react';
import { fetchAPI } from '@/api/fetch';
import { apiGetAdminProfile } from '@/api/services';
import logo from '@/assets/icon/logo-icon.svg';
import useAdminAuth from '@/store/adminAuth';
import { AdminPermission } from '@/types/enums';
import Sidebar, { NavItem } from '@/components/layout/Sidebar';

const ADMIN_NAV_ITEMS: Omit<NavItem, 'isActive'>[] = [
  {
    id: 0,
    name: 'Analysis',
    icon: <Binoculars />,
    to: '/admin/analysis'
  },
  {
    id: 1,
    name: 'Analysis Provider',
    icon: <Gear />,
    to: '/admin/provider'
  },
  {
    id: 2,
    name: 'Mailer',
    icon: <EnvelopeSimple />,
    to: '/admin/mailer'
  },
  {
    id: 3,
    name: 'Users',
    icon: <User />,
    to: '/admin/users'
  }
];

export default function AdminSidebar() {
  const { logout } = useAdminAuth();
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [error, setError] = useState<string | null>(null);

  const navItems = useMemo(() => {
    const items: Omit<NavItem, 'isActive'>[] = [];

    admin?.permissions?.forEach((permission) => {
      switch (permission) {
        case AdminPermission.ANALYSIS:
          items.push(ADMIN_NAV_ITEMS[0]);
          items.push(ADMIN_NAV_ITEMS[1]);
          break;
        case AdminPermission.MAILER:
          items.push(ADMIN_NAV_ITEMS[2]);
          break;
        case AdminPermission.USER:
          items.push(ADMIN_NAV_ITEMS[3]);
          break;
      }
    });

    return items;
  }, [admin?.permissions]);

  useEffect(() => {
    const fetchAdminProfile = async () => {
      try {
        setError(null);
        const response = await fetchAPI<Admin>({
          request: () => apiGetAdminProfile()
        });

        if (response.success) {
          setAdmin(response.data);
        } else {
          setError('Failed to fetch admin profile');
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
        setError(`Failed to fetch admin profile: ${errorMessage}`);
        console.error('Failed to fetch admin profile:', error);
      }
    };

    fetchAdminProfile();
  }, []);

  if (error) {
    console.error(error);
  }

  return (
    <Sidebar
      navItems={navItems}
      logo={logo}
      logoAlt="Admin Dashboard Logo"
      bgColor="primary.100"
      onLogout={logout}
    />
  );
}
